import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import TableHead from '@mui/material/TableHead';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDDateRange from 'components/MDDateRange';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'r.date_record',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'ALL',
  branch: 'ALL',
  supplier: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initSummary = {
  quantity: '',
  amount: '',
  discount: '',
  total_discount: '',
  net: '',
};

function RpPurchaseHistory() {
  const mod = 'rp_purchase_history';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboSupplier, setCboSupplier] = useState([]);
  const [summary, setSummary] = useState(initSummary);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
      supplier: data.supplier,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    let param = `download`;
    param += `?search=${find.search}`;
    param += `&branch=${find.branch}`;
    param += `&period=${find.period}`;
    param += find.period === 'ALL' ? '' : `&date_start=${find.date_start}`;
    param += find.period === 'ALL' ? '' : `&date_stop=${find.date_stop}`;
    param += `&supplier=${find.supplier}`;

    window.open(`${urlExcel}/${param}`, '_self');
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setCboSupplier(result.supplier);
        setUrlExcel(result.excel);
        view({ ...find, branch: result.branch_id });
      } else {
        // Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.supplier}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, supplier: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>เจ้าหนี้ :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboSupplier.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={5}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={12} md={12} lg={1}>
            <MDButton variant='gradient' color='success' onClick={excel}>
              <Icon>table_view</Icon>&nbsp;Excel
            </MDButton>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_power_bi'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' width={100}>
                      รหัสสินค้า
                    </TableCell>
                    <TableCell>ชื่อสินค้า</TableCell>
                    <TableCell align='center' width={100}>
                      วันที่เอกสาร
                    </TableCell>
                    <TableCell align='center' width={100}>
                      เลขที่เอกสาร
                    </TableCell>
                    <TableCell align='center' width={100}>
                      รหัสเจ้าหนี้
                    </TableCell>
                    <TableCell align='center' width={80}>
                      หน่วยนับ
                    </TableCell>
                    <TableCell align='center' width={80}>
                      ซื้อ
                    </TableCell>
                    <TableCell>แถม</TableCell>
                    <TableCell align='right' width={90}>
                      ราคา@
                    </TableCell>
                    <TableCell align='right' width={90}>
                      ส่วนลด
                    </TableCell>
                    <TableCell align='right' width={90}>
                      มูลค่า
                    </TableCell>
                    <TableCell align='right' width={90}>
                      ส่วนลดรวม
                    </TableCell>
                    <TableCell align='right' width={90}>
                      สุทธิ
                    </TableCell>
                    <TableCell align='right' width={90}>
                      ต่อหน่วย
                    </TableCell>
                    <TableCell align='center' width={100}>
                      รหัสซื้อขาย
                    </TableCell>
                    <TableCell>เจ้าหนี้</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell align='center' className='nowrap'>
                        {row.code}
                      </TableCell>
                      <TableCell className='nowrap'>{row.name}</TableCell>
                      <TableCell align='center'>{Func.dateDisplay(row.date_record)}</TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.receive_code}
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.supplier_code}
                      </TableCell>
                      <TableCell align='center'>{row.unit_name}</TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.quantity_receive)}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.quantity_free)}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.price, 2)}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {row.discount_unit > 0 ? Func.numberDisplay(row.discount_unit, 2) : '-'}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.total, 2)}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {row.discount > 0 ? Func.numberDisplay(row.discount, 2) : '-'}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.amount, 2)}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.per_unit, 2)}
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.barcode}
                      </TableCell>
                      <TableCell className='nowrap'>{row.supplier_name}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell align='right' colSpan={6}>
                      รวม
                    </TableCell>
                    <TableCell align='right' className='color-primary'>
                      {Func.numberDisplay(summary.quantity)}
                    </TableCell>
                    <TableCell colSpan={1} />
                    <TableCell align='right'>{Func.numberDisplay(summary.avg_price, 2)}</TableCell>
                    <TableCell align='right'>
                      {Func.numberDisplay(summary.avg_discount_unit, 2)}
                    </TableCell>
                    <TableCell align='right'>{Func.numberDisplay(summary.amount, 2)}</TableCell>
                    <TableCell align='right'>
                      {summary.total_discount > 0
                        ? Func.numberDisplay(summary.total_discount, 2)
                        : '-'}
                    </TableCell>
                    <TableCell align='right'>{Func.numberDisplay(summary.net, 2)}</TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_power_bi`), 'รายงานประวัติการสั่งซื้อ']} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpPurchaseHistory;
